import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { TinsmithLockup } from "@tinsmith/brand";

import styles from "../../auth/AuthScreen.module.css";
import { useDocumentTitle } from "../../chrome/useDocumentTitle";
import { Button, ButtonLink } from "../../components/Button";
import { confirmEmailChange } from "../../data/api/account";
import { ApiError } from "../../data/api/client";
import { paths } from "../../routing/paths";

// Where an email-change confirmation link lands (auth-and-sessions.md §Email
// change). The change happens when the button below is pressed, never on
// arrival: a mail scanner, a link-rewriter or a prefetcher fetches the page,
// and none of them can press it.
//
// Public, like `/join` — the link goes to the *new* address, which may well be
// read in a browser this account has never signed in from.

type Status = "confirmed" | "confirming" | "ready";

export const ConfirmEmailPage = () => {
  const [params, setParams] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState<Status>("ready");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  useDocumentTitle("Confirm email");

  const confirm = (): void => {
    if (!token) {
      return;
    }
    setStatus("confirming");
    setError(null);
    void confirmEmailChange(token)
      .then((result) => {
        setEmail(result.email);
        setStatus("confirmed");
        // Out of the address bar now it is spent, replacing the history entry
        // the mail client's click created rather than adding one — a token is a
        // secret, and browser history is not where secrets should settle. Only
        // once it has worked, so a failed attempt can still be retried.
        setParams(new URLSearchParams(), { replace: true });
      })
      .catch((e: unknown) => {
        setError(
          e instanceof ApiError
            ? e.message
            : "Couldn't confirm your new email address.",
        );
        setStatus("ready");
      });
  };

  return (
    <div className={styles.screen}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <TinsmithLockup size={24} />
        </div>

        {status === "confirmed" ? (
          <>
            <h1 className={styles.title}>Email confirmed</h1>
            <p className={styles.subtitle}>
              Your Tinsmith account now uses <strong>{email}</strong>. Every
              other signed-in browser has been signed out.
            </p>
            <ButtonLink size="lg" to={paths.dashboard()} variant="accent">
              Go to Tinsmith
            </ButtonLink>
          </>
        ) : !token ? (
          <>
            <h1 className={styles.title}>Confirmation link incomplete</h1>
            <p className={styles.subtitle}>
              This link is missing its token. Open the confirmation email again
              and follow the link from there.
            </p>
            <ButtonLink size="lg" to={paths.dashboard()} variant="accent">
              Go to Tinsmith
            </ButtonLink>
          </>
        ) : (
          <>
            <h1 className={styles.title}>Confirm your new email address</h1>
            <p className={styles.subtitle}>
              You asked to move your Tinsmith account to the address this link
              was sent to. Nothing has changed yet.
            </p>
            <div className={styles.callout}>
              When you confirm, your account starts using the new address,{" "}
              <strong>every other signed-in browser</strong> is signed out, and
              your previous address is notified of the change.
            </div>
            {error ? <div className={styles.error}>{error}</div> : null}
            <Button
              className={styles.submit}
              disabled={status === "confirming"}
              onClick={confirm}
              size="lg"
              variant="accent"
            >
              {status === "confirming" ? "Confirming…" : "Confirm the change"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
